import Typography from '@mui/material/Typography';
import { Button, Divider, Grid, useMediaQuery } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { FormTextField } from '../../experienceSettings/formComponents/FormTextField';

import { AccountData, OrderPageTypes } from '../../api';
import { useAuth } from '../../session/InternalAuthProvider';
import { getCurrencySymbol } from '../../utils/utils';
import { useLanguage } from '../../common/GeneralUtils';
import { mb } from '../../common/theme/mbTokens';

type OrderPageDetailsProps = {
  handleBack: () => void;
  baseValue?: number;
  isSubmitting: boolean;
  orderPageType: OrderPageTypes;
};
export const OrderPageDetails = ({ handleBack, baseValue, isSubmitting, orderPageType }: OrderPageDetailsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const textBoxWidth = isSmallScreen ? 300 : 500;
  const languages = account.customer_facing_widgets_languages;
  const language = useLanguage();
  const activityLabel = account?.activity_label_singular?.[language];

  const currencySymbol = getCurrencySymbol(authState.account as unknown as AccountData);
  return (
    <Grid container flexDirection="column" sx={{ p: mb.layout.cardPadding }}>
      <Typography variant="h5">{t('editOrderPage.pricing')}</Typography>
      <Typography variant="body2" color="warning.dark">
        {t('editOrderPage.pricingAlert')}
      </Typography>
      <Divider />
      <Grid container alignItems="center" gap={1} mt={2}>
        <Grid item>
          <Typography>{t('editOrderPage.basePrice')}</Typography>
        </Grid>
        <Grid item>
          <FormTextField
            type="number"
            width={100}
            fieldName="base_price"
            label={''}
            endAdornment={currencySymbol}
            disabled={isSubmitting}
            isFieldUpdate={false}
          />
        </Grid>
        <Typography>{t('editOrderPage.for')}</Typography>
        <Grid item>
          <FormTextField
            type="number"
            width={100}
            fieldName={`base_value`}
            label={''}
            disabled={isSubmitting}
            isFieldUpdate={false}
          />
        </Grid>
        <Grid item>
          <Typography>
            {baseValue && baseValue === 1 ? t('editOrderPage.person') : t('editOrderPage.persons')}
          </Typography>
        </Grid>
      </Grid>

      <Grid container alignItems="center" gap={1} mt={2}>
        <Typography>{t('editOrderPage.forAdditional')}</Typography>
        <Grid item>
          <FormTextField
            type="number"
            width={100}
            fieldName={`price_per_additional_value`}
            endAdornment={currencySymbol}
            label={''}
            disabled={isSubmitting}
            isFieldUpdate={false}
          />
        </Grid>
      </Grid>

      <Grid container my={3} gap={2} flexDirection="column">
        <Typography variant="h5">{t('editOrderPage.basicInformation')}</Typography>
        <Divider />

        {languages.map((lang) => (
          <Grid key={lang}>
            <FormTextField
              key={`label.${lang}`}
              width={textBoxWidth}
              label={t(`editOrderPage.orderPageNameLang.${lang}`)}
              fieldName={`label.${lang}`}
              caption={t('editOrderPage.orderPageNameCaption', { activityLabel })}
              disabled={isSubmitting}
              isFieldUpdate={false}
            />
          </Grid>
        ))}
      </Grid>

      {orderPageType === OrderPageTypes.COURSE && (
        <Grid container my={3} gap={2} flexDirection="column">
          <Typography variant="h5">{t('editOrderPage.courseSettings')}</Typography>
          <Divider />
          <Grid container alignItems="center">
            <FormTextField
              type="number"
              width={200}
              fieldName="course_sessions_number"
              label={t('editOrderPage.courseSessionsNumber')}
              disabled={isSubmitting}
              isFieldUpdate={false}
            />
          </Grid>
        </Grid>
      )}

      <Grid container justifyContent="space-between">
        <Button variant="outlined" color="inherit" onClick={handleBack}>
          {t('back')}
        </Button>
        <LoadingButton size="large" loading={isSubmitting} type="submit" variant="contained" color="primary">
          {t('editOrderPage.createOrderPage')}
        </LoadingButton>
      </Grid>
    </Grid>
  );
};
